import classNames from 'classnames'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'

import {
  trackWaitingListInnerRoute,
  waitingListCheckConfirmationCodeInnerRoute,
  waitingListSendConfirmationEmailInnerRoute,
} from '../../../api/auth'
import CompletedIcon from '../../../assets/icons/sidebar-layout/check.svg'
import BackIcon from '../../../assets/icons/signup/back.svg'
import { useCurrentUser } from '../../../context/CurrentUserContext'
import { useGlobalToolConfig } from '../../../context/GlobalToolConfigContext'
import { useInternationalization } from '../../../context/InternationalizationContext'
import useStorageState from '../../../hooks/useStorageState'
import { trackMixpanelEvent_requestEarlyAccessClick } from '../../../utils/tracking'
import CircleLoader from '../../common/CircleLoader/CircleLoader'
import CodeInput from '../../signup/login/LoginEmailForm/CodeInput'
import EmailInput from '../../signup/login/LoginEmailForm/EmailInput'
import { SIDEBAR_TAB_KEYS } from '../SidebarLayout'
import styles from './SidebarLayoutComponents.module.scss'

const NotifyAboutSoonToolModal = () => {
  const { currentUser } = useCurrentUser()
  const { activeSidebarTab } = useGlobalToolConfig()
  const { locale, getText } = useInternationalization()

  const [email, setEmail] = useStorageState(currentUser?.email || '', 'waiting-list-email')
  const [emailNotValid, setEmailNotValid] = useState(false)

  const [code, setCode] = useState('_______')
  const [codeNotValid, setCodeNotValid] = useState(false)

  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const [waitingList, setWaitingList] = useStorageState([] as any[], 'waiting-list')
  const [status, setStatus] = useState<'default' | 'in-progress' | 'completed'>('default')

  useEffect(() => {
    const isAlreadyInWaitingList = waitingList.includes(activeSidebarTab.key)
    setStatus(isAlreadyInWaitingList ? 'completed' : 'default')
  }, [activeSidebarTab, waitingList])
  useEffect(() => {
    const dbWaitingList = currentUser?.prefs.waitingList || []
    if (dbWaitingList.length && waitingList.every((tool) => dbWaitingList.includes(tool))) setWaitingList(dbWaitingList)
  }, [currentUser])
  useEffect(() => {
    if (currentUser && !email) {
      setEmail(currentUser.email)
    }
  }, [])

  const STRINGS = getText('NotifyAboutSoonToolModal')

  const handleSendConfirmationEmail = async () => {
    setLoading(true)

    const isEmailConfirmed = await waitingListSendConfirmationEmailInnerRoute(email, locale || '')
    trackMixpanelEvent_requestEarlyAccessClick(activeSidebarTab.key)

    if (isEmailConfirmed) {
      await handleJoinWaitingList(isEmailConfirmed)
    } else {
      setStatus('in-progress')
    }

    setLoading(false)
  }
  const handleJoinWaitingList = async (validProps = false) => {
    setLoading(true)

    let valid = validProps
    if (!valid) valid = await waitingListCheckConfirmationCodeInnerRoute(email, code)

    if (valid) {
      const newWaitingList = Array.from(new Set([...waitingList, activeSidebarTab.key]))

      await trackWaitingListInnerRoute(email, activeSidebarTab.key, locale, newWaitingList)
      setWaitingList(newWaitingList)
      setStatus('completed')
    } else {
      setError(STRINGS.codeError)
    }

    setLoading(false)
  }
  const handleReturnToFirstStep = () => {
    setStatus('default')
  }

  const renderHeader = (title: string, cb: () => void) => {
    return (
      <div className={styles.notifyMeModalHeader}>
        <div className={styles.notifyMeModalHeaderBack} onClick={cb}>
          <BackIcon />
        </div>
        <div className={styles.notifyMeModalHeaderTitle}>{title}</div>
      </div>
    )
  }
  const renderDetails = (title: string, text: string) => {
    return (
      <div className={styles.notifyMeModalDetails}>
        <div className={styles.notifyMeModalTitle}>{title}</div>
        <div className={styles.notifyMeModalText}>{text.replace('___', activeSidebarTab.name)}</div>
      </div>
    )
  }

  const renderEmailInput = () => {
    return (
      <div className={styles.notifyMeModalInput}>
        <EmailInput
          email={email}
          setEmail={setEmail}
          error={error}
          setError={setError}
          emailNotValid={emailNotValid}
          setEmailNotValid={setEmailNotValid}
        />
      </div>
    )
  }
  const renderCodeInput = () => {
    return (
      <div className={styles.notifyMeModalInput}>
        <CodeInput
          code={code}
          setCode={setCode}
          error={error}
          setError={setError}
          codeNotValid={codeNotValid}
          setCodeNotValid={setCodeNotValid}
        />
      </div>
    )
  }

  const renderDefaultBtn = () => {
    return (
      <button
        className={styles.notifyMeSubmitBtn}
        disabled={emailNotValid || !email || loading}
        onClick={handleSendConfirmationEmail}
      >
        {loading && <CircleLoader />}
        <span>{STRINGS.defaultBtn}</span>
      </button>
    )
  }
  const renderDefaultScreen = () => {
    if (status !== 'default') return null
    return (
      <>
        <div className={styles.notifyMeModalTop}>
          <div className={classNames(styles.notifyMeModalIcon, styles[`notifyMeModalIcon_${activeSidebarTab.key}`])}>
            <div className={styles.notifyMeModalIconFirstLayer} />
            <div className={styles.notifyMeModalIconSecondLayer} />
            <div className={styles.notifyMeModalIconThirdLayer}>{activeSidebarTab.icon}</div>
          </div>

          {renderDetails(activeSidebarTab.name, STRINGS[`defaultText_${activeSidebarTab.key}`])}

          {renderEmailInput()}
        </div>

        <div className={styles.notifyMeModalBottom}>{renderDefaultBtn()}</div>
      </>
    )
  }

  const renderInProgressBtn = () => {
    return (
      <button
        className={classNames(styles.notifyMeSubmitBtn, styles.notifyMeSubmitBtnBlue)}
        disabled={codeNotValid || !code || loading}
        onClick={() => handleJoinWaitingList()}
      >
        {loading && <CircleLoader />}
        <span>{STRINGS.inProgressBtn}</span>
      </button>
    )
  }
  const renderInProgressScreen = () => {
    if (status !== 'in-progress') return null
    return (
      <>
        {renderHeader(STRINGS.inProgressTitle, () => setStatus('default'))}

        <div className={styles.notifyMeModalTop}>
          <Image layout={'fixed'} width={150} height={120} src='/static/images/login/letter.svg' alt='' />

          <div className={styles.notifyMeModalTextInProgress}>{STRINGS.inProgressText.replace('___', email)}</div>

          {renderCodeInput()}
        </div>

        <div className={styles.notifyMeModalBottom}>{renderInProgressBtn()}</div>
      </>
    )
  }

  const renderCompletedBtn = () => {
    return (
      <button
        className={classNames(styles.notifyMeSubmitBtn, styles.notifyMeSubmitBtnNoColor)}
        onClick={handleReturnToFirstStep}
      >
        {STRINGS.successBtn}
      </button>
    )
  }
  const renderCompletedScreen = () => {
    if (status !== 'completed') return null
    return (
      <>
        <div className={styles.notifyMeModalTop}>
          <div
            className={classNames(styles.notifyMeModalIcon, styles[`notifyMeModalIcon_${SIDEBAR_TAB_KEYS.MASTERING}`])}
          >
            <div className={styles.notifyMeModalIconFirstLayer} />
            <div className={styles.notifyMeModalIconSecondLayer} />
            <div className={styles.notifyMeModalIconThirdLayer}>
              <CompletedIcon />
            </div>
          </div>

          {renderDetails(STRINGS.successTitle, STRINGS.successText)}
        </div>

        <div className={styles.notifyMeModalBottom}>{renderCompletedBtn()}</div>
      </>
    )
  }

  if (!activeSidebarTab.isSoon) return null
  return (
    <div className={styles.notifyMeModalContainer}>
      <div className={styles.notifyMeModal}>
        {renderDefaultScreen()}
        {renderInProgressScreen()}
        {renderCompletedScreen()}
      </div>
    </div>
  )
}

export default NotifyAboutSoonToolModal
